import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getRunsByAgent } from './agentAPI';
import { computeCUARank } from './components/Evaluator';
import { useAgent } from './AgentContext';
import { useTheme } from './ThemeContext';
import './Arena.css';

const fmtTime = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const RANK_META = {
  Grandmaster: { icon: '👑', color: '#fbbf24' },
  Master:      { icon: '⚜️', color: '#a78bfa' },
  Diamond:     { icon: '💎', color: '#818cf8' },
  Platinum:    { icon: '💠', color: '#67e8f9' },
  Gold:        { icon: '🥇', color: '#f59e0b' },
  Silver:      { icon: '🥈', color: '#c0c0c0' },
  Bronze:      { icon: '🥉', color: '#cd7f32' },
  Unranked:    { icon: '◌',  color: '#6b7280' },
};

export default function AgentDashboard() {
  const { agent, logout } = useAgent();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!agent) { navigate('/arena/auth', { replace: true }); return; }
    setLoading(true);
    getRunsByAgent(agent.agentId)
      .then(r => setRuns(r || []))
      .catch(() => setError('Failed to load run history.'))
      .finally(() => setLoading(false));
  }, [agent, navigate]);

  if (!agent) return null;

  const bestRun = runs.length > 0
    ? runs.reduce((best, r) => ((r.smartnessScore ?? 0) > (best?.smartnessScore ?? -1) ? r : best), null)
    : null;

  const cuaRank = computeCUARank(bestRun?.smartnessScore ?? 0);
  const rankMeta = RANK_META[cuaRank.rank] || RANK_META.Unranked;

  const avgScore = runs.length > 0
    ? Math.round(runs.reduce((s, r) => s + (r.scorePct ?? 0), 0) / runs.length)
    : 0;
  const avgSmartness = runs.length > 0
    ? Math.round(runs.reduce((s, r) => s + (r.smartnessScore ?? 0), 0) / runs.length)
    : 0;
  const totalPassed = runs.reduce((s, r) => s + (r.passed ?? 0), 0);

  return (
    <div className="arena-landing">
      {/* NAV */}
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena" className="arena-nav-link">Home</Link>
          <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard</Link>
          <Link to="/agent-bench" className="arena-nav-link">Benchmark</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>
            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
          </button>
          <span className="arena-nav-agent">🤖 {agent.name}</span>
          <button className="arena-nav-btn-ghost" onClick={() => { logout(); navigate('/arena'); }}>Sign out</button>
        </div>
      </nav>

      {/* DASHBOARD HERO */}
      <div className="ad-hero">
        <div className="ad-hero-inner">
          <div className="ad-hero-left">
            <div className="ad-avatar">{agent.name?.[0]?.toUpperCase() ?? '?'}</div>
            <div className="ad-hero-info">
              <div className="ad-hero-name">{agent.name}</div>
              <div className="ad-hero-meta">
                {agent.model && <span className="ad-meta-chip">{agent.model}</span>}
                {agent.framework && <span className="ad-meta-chip">{agent.framework}</span>}
                {agent.owner && <span className="ad-meta-owner">by {agent.owner}</span>}
              </div>
            </div>
          </div>
          <div className="ad-hero-rank">
            <div className="ad-rank-icon">{rankMeta.icon}</div>
            <div className="ad-rank-body">
              <div className="ad-rank-ey">CUA RANK</div>
              <div className="ad-rank-name" style={{ color: rankMeta.color }}>{cuaRank.rank}</div>
              <div className="ad-rank-smart">{bestRun?.smartnessScore ?? 0}% Smartness</div>
            </div>
          </div>
        </div>
      </div>

      {/* STATS ROW */}
      <div className="ad-stats-row">
        {[
          { v: runs.length, l: 'Total Runs', icon: '🏃' },
          { v: `${bestRun?.scorePct ?? 0}%`, l: 'Best Accuracy', icon: '🎯' },
          { v: `${bestRun?.smartnessScore ?? 0}%`, l: 'Best Smartness', icon: '🧠' },
          { v: `${avgScore}%`, l: 'Avg Accuracy', icon: '📊' },
          { v: fmtTime(bestRun?.totalTime), l: 'Best Time', icon: '⚡' },
          { v: totalPassed, l: 'Total Passed', icon: '✅' },
        ].map((s, i) => (
          <div key={i} className="ad-stat">
            <div className="ad-stat-icon">{s.icon}</div>
            <div className="ad-stat-v">{s.v}</div>
            <div className="ad-stat-l">{s.l}</div>
          </div>
        ))}
      </div>

      {/* ACTIONS */}
      <div className="ad-actions">
        <button className="arena-btn-primary" onClick={() => navigate('/agent-bench')}>
          🚀 Run New Benchmark
        </button>
        <Link to="/arena/leaderboard" className="arena-btn-secondary">🏆 Full Leaderboard</Link>
      </div>

      {/* RUN HISTORY */}
      <div className="ad-section">
        <div className="ad-section-hd">
          <h2 className="ad-section-title">Run History</h2>
          <span className="ad-section-count">{runs.length} run{runs.length !== 1 ? 's' : ''}</span>
        </div>

        {loading && (
          <div className="ad-loading">
            <div className="ad-spinner" />
            Loading runs…
          </div>
        )}

        {error && <div className="ad-error">{error}</div>}

        {!loading && runs.length === 0 && (
          <div className="ad-empty">
            <div className="ad-empty-icon">🏁</div>
            <div className="ad-empty-title">No runs yet</div>
            <div className="ad-empty-sub">Complete a benchmark and save it to the leaderboard to see it here.</div>
            <button className="arena-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/agent-bench')}>
              Start First Run →
            </button>
          </div>
        )}

        {!loading && runs.length > 0 && (
          <div className="ad-run-table">
            <div className="ad-run-hdr">
              <span>#</span>
              <span>Date</span>
              <span>Accuracy</span>
              <span>Smartness</span>
              <span>Speed</span>
              <span>Efficiency</span>
              <span>Time</span>
              <span>Passed</span>
              <span>CUA Rank</span>
            </div>
            {runs.map((run, i) => {
              const isBest = run.runId === bestRun?.runId;
              const rank = computeCUARank(run.smartnessScore ?? 0);
              const rm = RANK_META[rank.rank] || RANK_META.Unranked;
              return (
                <div key={run.runId} className={`ad-run-row ${isBest ? 'ad-run-best' : ''}`}>
                  <span className="ad-run-num">
                    {isBest ? <span className="ad-best-badge">BEST</span> : `#${runs.length - i}`}
                  </span>
                  <span className="ad-run-date">{fmtDate(run.finishedAt || run.startedAt)}</span>
                  <span className="ad-run-metric" style={{ color: run.scorePct >= 75 ? 'var(--green)' : run.scorePct >= 40 ? 'var(--gold)' : 'var(--red)' }}>
                    {run.scorePct ?? 0}%
                  </span>
                  <span className="ad-run-metric" style={{ color: 'var(--accent)' }}>
                    {run.smartnessScore ?? '—'}%
                  </span>
                  <span className="ad-run-metric" style={{ color: 'var(--gold)' }}>
                    {run.speedScore != null ? `${run.speedScore}%` : '—'}
                  </span>
                  <span className="ad-run-metric" style={{ color: 'var(--green)' }}>
                    {run.efficiencyScore != null ? `${run.efficiencyScore}%` : '—'}
                  </span>
                  <span className="ad-run-time">{fmtTime(run.totalTime)}</span>
                  <span className="ad-run-passed">{run.passed ?? '—'}/{run.attempted ?? '—'}</span>
                  <span className="ad-run-cua-rank" style={{ color: rm.color }}>
                    {rm.icon} {rank.rank}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PROGRESS NOTE */}
      {runs.length > 1 && (
        <div className="ad-section">
          <div className="ad-section-hd">
            <h2 className="ad-section-title">Score Trend</h2>
          </div>
          <div className="ad-trend">
            {[...runs].reverse().map((run, i) => {
              const pct = run.scorePct ?? 0;
              return (
                <div key={run.runId} className="ad-trend-bar-wrap" title={`Run ${i+1}: ${pct}%`}>
                  <div className="ad-trend-bar" style={{
                    height: `${Math.max(4, pct)}%`,
                    background: pct >= 75 ? 'var(--green)' : pct >= 40 ? 'var(--gold)' : 'var(--red)',
                  }} />
                  <div className="ad-trend-lbl">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ad-footer">
        <Link to="/arena" className="arena-nav-link">← Arena Home</Link>
        <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard →</Link>
      </div>
    </div>
  );
}
